<template>
  <div v-if="showDocument" class="legal-document">
    <Loading v-if="loadingDocument" />
    <div v-else v-html="documentContent.content" />
  </div>
  <div v-else>
    <div>
      {{ $t('bloodReport.infoStep.perex') }}
    </div>
    <div class="my-6 grey rounded-lg pa-4">
      <div
        v-for="(item, index) in $t('bloodReport.infoStep.list', { returnObjects: true })"
        :key="`points_${index}`"
        class="d-flex align-center"
        :class="{
          'pb-4': index < $t('bloodReport.infoStep.list', { returnObjects: true }).length - 1
        }"
      >
        <div class="mr-4">
          <v-img width="48" height="48" contain :src="$asset(`img/icons/${item.icon}.svg`)" />
        </div>
        <div class="py-0 text-left">{{ item.text }}</div>
      </div>
    </div>
    <i18n
      v-for="(_, index) in $t('bloodReport.infoStep.legal')"
      :key="`disclaimer_${index}`"
      :path="`bloodReport.infoStep.legal[${index}]`"
      tag="p"
      class="grey-darken-1--text text-body-small text-left"
    >
      <template #link>
        <span
          class="link--inline grey-darken-1--text"
          @click.prevent.stop="showLegalDocument('terms_and_conditions', index)"
          >{{ $t(`bloodReport.infoStep.legalLinkTexts[${index}]`) }}</span
        >
      </template>
    </i18n>
    <div class="mt-6 mb-6">
      <v-checkbox
        v-model="$v.legalAgreementTermsAndConditions.$model"
        hide-details
        :error="$v.legalAgreementTermsAndConditions.$error"
      >
        <template #label>
          <i18n path="bloodReport.infoStep.agreements.termsAndConditions.text">
            <template #link>
              <span
                class="link--inline black--text"
                @click.prevent.stop="showLegalDocument('healthcare')"
                >{{ $t('bloodReport.infoStep.agreements.termsAndConditions.linkText') }}</span
              >
            </template>
          </i18n>
        </template>
      </v-checkbox>
    </div>
    <DocumentModal
      v-for="(document, key) in legalDocuments"
      :key="`documents_${key}`"
      identifier="videocall"
      :name="document.name"
      category="videocall"
      :type-name="document.type"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import { checked } from '@/customVuelidate';
import legalDocuments from '@/mixins/legalDocuments';

export default {
  name: 'BloodTestInfoStep',
  mixins: [legalDocuments],
  props: {
    formName: {
      type: String,
      required: true
    }
  },
  validations() {
    return {
      legalAgreementTermsAndConditions: { required, checked }
    };
  },
  data() {
    return {
      loading: false,
      loadingDocument: false,
      legalAgreementTermsAndConditions: false,
      showDocument: false,
      documentContent: {
        content: ''
      },
      type: ''
    };
  },
  computed: {
    legalDocuments() {
      return [
        {
          name: 'personal_data_processing',
          type: 'personal_data_processing'
        }
      ];
    }
  },
  watch: {
    loading() {
      this.setDynamicDialogButtons();
    },
    async showDocument(newValue) {
      if (newValue && this.type !== 'serviceText') {
        this.loadingDocument = true;
        await this.setDialogTitle(null);
        this.documentContent = await this.getLegalDocument('videocall', this.type);
        await this.setDialogTitle(
          this.documentContent && this.documentContent.title ? this.documentContent.title : null
        );
        this.loadingDocument = false;
      }
    }
  },
  mounted() {
    this.setDynamicDialogButtons();
    this.setDialogDefaultSetup();
  },
  methods: {
    ...mapActions({
      setDialogTitle: 'profile/bloodTestForm/setDialogTitle',
      setDialogButtons: 'profile/bloodTestForm/setDialogButtons',
      setClickBack: 'profile/bloodTestForm/setClickBack',
      setDialogHeaderType: 'profile/bloodTestForm/setDialogHeaderType',
      setDialogLeftHeaderIcon: 'profile/bloodTestForm/setDialogLeftHeaderIcon',
      setShowBackArrow: 'profile/bloodTestForm/setShowBackArrow'
    }),
    setDialogDefaultSetup() {
      this.setDialogTitle(this.$t('bloodReport.infoStep.title'));
      this.setDialogLeftHeaderIcon('icons/report.svg');
      this.setDialogHeaderType('information');
      this.setShowBackArrow(false);
      this.setClickBack(undefined);
      this.showDocument = false;
    },
    showLegalDocument(type, legalIndex) {
      this.setClickBack(() => {
        this.setDynamicDialogButtons();
        this.setDialogDefaultSetup();
      });
      this.setDialogLeftHeaderIcon(null);
      this.setDialogHeaderType('navigation');
      this.setDialogButtons([]);
      this.setShowBackArrow(true);

      switch (legalIndex) {
        case 0:
          this.documentContent.content = this.$t('bloodReport.infoStep.serviceText');
          this.type = 'serviceText';
          break;
        case 2:
          this.type = 'personal_data_processing';
          break;
        default:
          this.type = type;
      }

      this.showDocument = true;
    },
    async nextStep() {
      this.loading = true;
      this.$store.commit('forms/emergencyDisclaimer/setIsEmergencyChecked', true);
      const personalDataProcessing = await this.getLegalDocument(
        'videocall',
        'personal_data_processing'
      );
      const termsConditions = await this.getLegalDocument('videocall', 'terms_and_conditions');

      await this.$http.post('/v3/legal-documents/' + personalDataProcessing.id + '/confirm');
      await this.$http.post('/v3/legal-documents/' + termsConditions.id + '/confirm');

      this.loading = false;
      this.$emit('next-click');
    },
    setDynamicDialogButtons() {
      this.setDialogButtons([
        {
          type: 'sendButton',
          props: {
            loading: this.loading
          },
          events: {
            click: async () => {
              this.$v.$touch();
              if (!this.$v.$invalid) {
                await this.nextStep();
              } else {
                this.$nextTick(() => this.scrollToError(-100, '.dialog-main-content'));
              }
            }
          },
          text: this.$t('goNext')
        }
      ]);
    }
  }
};
</script>
